import type { TPlayer } from 'video.js'
import type { Events } from '|>/shared/events'
import type { Settings } from './settings'

import { bind_ } from '|>/shared/fn'
import { register } from '|>/shared/vjs'
import { BaseComponent } from '../base'
import { SettingsAudioMenu } from './audio/settings-audio-menu'
import { LocalEvents } from './local-events'
import { SettingsMainMenu } from './main/settings-main-menu'
import { autoQuality } from './quality/helpers'
import { SettingsQualityMenu } from './quality/settings-quality-menu'
import { SettingsSubtitlesCustomMenu } from './subtitles/settings-subtitles-custom-menu'
import { SettingsSubtitlesLanguageMenu } from './subtitles/settings-subtitles-language-menu'
import { SettingsSubtitlesMenu } from './subtitles/settings-subtitles-menu'

/**
 * Settings menu with audio and subtitles options
 */
@register
export class SettingsMenuContainer extends BaseComponent {
  declare settings: Settings

  // menus
  declare mainMenu: SettingsMainMenu
  declare audioMenu: SettingsAudioMenu
  declare subtitlesMenu: SettingsSubtitlesMenu
  declare qualityMenu: SettingsQualityMenu
  declare subtitlesLanguageMenu: SettingsSubtitlesLanguageMenu
  declare subtitlesCustomMenu: SettingsSubtitlesCustomMenu
  // state
  declare qualityState: Events.Media.QualityLevelSelected

  private isDesktop_: boolean
  private handleMouseEnter_: (() => void) | null = null
  private handleMouseLeave_: (() => void) | null = null

  constructor(player: TPlayer, options: any) {
    super(player, options)
    this.hide()

    this.qualityState = autoQuality

    // set link to this container for child menus
    this.mainMenu.container = this
    this.audioMenu.container = this
    this.subtitlesMenu.container = this
    this.qualityMenu.container = this
    this.subtitlesLanguageMenu.container = this
    this.subtitlesCustomMenu.container = this
    // listen for local events
    const activateMainMenu = bind_(this, this.activateMainMenu)
    const activateAudioMenu = bind_(this, this.activateAudioMenu)
    const activateSubtitlesMenu = bind_(this, this.activateSubtitlesMenu)
    const activateSubtitlesLanguageMenu = bind_(
      this,
      this.activateSubtitlesLanguageMenu
    )
    const activateQualityMenu = bind_(this, this.activateQualityMenu)
    const activateSubtitlesCustomMenu = bind_(
      this,
      this.activateSubtitlesCustomMenu
    )
    const closeMenu = bind_(this, this.closeMenu)

    this.on(LocalEvents.SettingsActivateMainMenu, activateMainMenu)
    this.on(LocalEvents.SettingsActivateAudioMenu, activateAudioMenu)
    this.on(LocalEvents.SettingsActivateSubtitlesMenu, activateSubtitlesMenu)
    this.on(
      LocalEvents.SettingsActivateSubtitlesLanguageMenu,
      activateSubtitlesLanguageMenu
    )
    this.on(
      LocalEvents.SettingsActivateSubtitlesCustomMenu,
      activateSubtitlesCustomMenu
    )
    this.on(LocalEvents.SettingsActivateQualityMenu, activateQualityMenu)
    this.on(LocalEvents.SettingsCloseMenu, closeMenu)

    // Check if device supports hover (desktop)
    this.isDesktop_ = window.matchMedia('(hover: hover)').matches
    // Setup hover handlers for desktop
    if (this.isDesktop_) {
      this.handleMouseEnter_ = bind_(this, this.handleMouseEnter)
      this.handleMouseLeave_ = bind_(this, this.handleMouseLeave)
      this.el_.addEventListener('mouseenter', this.handleMouseEnter_)
      this.el_.addEventListener('mouseleave', this.handleMouseLeave_)
    }

    this.update()
  }

  update() {
    this.mainMenu.update()
    this.audioMenu.update()
    this.subtitlesMenu.update()
    this.subtitlesLanguageMenu.update()
    this.subtitlesCustomMenu.update()
    this.qualityMenu.update()
  }

  getSubtitlesMenuLabel(): string {
    return this.mainMenu.subtitlesItem.options_.label
  }

  override show() {
    super.show()
    this.player_.addClass('vjs-menu-opened')
  }

  override hide() {
    super.hide()
    this.player_.removeClass('vjs-menu-opened')
  }

  activateMainMenu() {
    this.show()

    // set height to current height for later transition
    const currentHeight = this.el_.offsetHeight
    this.el_.style.height = `${currentHeight}px`

    this.audioMenu.deactivate()
    this.subtitlesMenu.deactivate()
    this.subtitlesLanguageMenu.deactivate()
    this.subtitlesCustomMenu.deactivate()
    this.qualityMenu.deactivate()
    this.mainMenu.activate()
    this.settings.gearButton.pressed(true)

    // height transition
    this.requestAnimationFrame(() => {
      this.el_.style.height = `${this.mainMenu.el_.scrollHeight}px`
    })
  }

  activateAudioMenu() {
    this.show()

    // set height to current height for later transition
    const currentHeight = this.el_.offsetHeight
    this.el_.style.height = `${currentHeight}px`

    this.mainMenu.deactivate()
    this.subtitlesMenu.deactivate()
    this.subtitlesLanguageMenu.deactivate()
    this.subtitlesCustomMenu.deactivate()
    this.qualityMenu.deactivate()
    this.audioMenu.activate()
    this.settings.gearButton.pressed(true)

    // height transition
    this.requestAnimationFrame(() => {
      this.el_.style.height = `${this.audioMenu.menu?.el_?.scrollHeight}px`
    })
  }

  activateSubtitlesMenu() {
    this.show()

    // set height to current height for later transition
    const currentHeight = this.el_.offsetHeight
    this.el_.style.height = `${currentHeight}px`

    this.mainMenu.deactivate()
    this.audioMenu.deactivate()
    this.subtitlesCustomMenu.deactivate()
    this.qualityMenu.deactivate()
    this.subtitlesMenu.activate()
    this.subtitlesLanguageMenu.deactivate()
    this.settings.gearButton.pressed(true)

    // height transition
    this.requestAnimationFrame(() => {
      this.el_.style.height = `${this.subtitlesMenu.el_.scrollHeight}px`
    })
  }

  activateQualityMenu() {
    this.show()

    // set height to current height for later transition
    const currentHeight = this.el_.offsetHeight
    this.el_.style.height = `${currentHeight}px`

    this.mainMenu.deactivate()
    this.audioMenu.deactivate()
    this.subtitlesMenu.deactivate()
    this.subtitlesLanguageMenu.deactivate()
    this.subtitlesCustomMenu.deactivate()
    this.qualityMenu.activate()
    this.settings.gearButton.pressed(true)

    // height transition
    this.requestAnimationFrame(() => {
      this.el_.style.height = `${this.qualityMenu.menu?.el_.scrollHeight}px`
    })
  }

  activateSubtitlesLanguageMenu() {
    this.show()

    // set height to current height for later transition
    const currentHeight = this.el_.offsetHeight
    this.el_.style.height = `${currentHeight}px`

    this.mainMenu.deactivate()
    this.audioMenu.deactivate()
    this.subtitlesMenu.deactivate()
    this.subtitlesCustomMenu.deactivate()
    this.qualityMenu.deactivate()
    this.subtitlesLanguageMenu.activate()
    this.settings.gearButton.pressed(true)

    // height transition
    this.requestAnimationFrame(() => {
      this.el_.style.height = `${this.subtitlesLanguageMenu.menu?.el_.scrollHeight}px`
    })
  }

  activateSubtitlesCustomMenu() {
    this.show()

    this.mainMenu.deactivate()
    this.audioMenu.deactivate()
    this.subtitlesMenu.deactivate()
    this.qualityMenu.deactivate()
    this.subtitlesLanguageMenu.deactivate()
    this.subtitlesCustomMenu.activate()
    this.settings.gearButton.pressed(true)
  }

  handleMouseEnter() {
    // Clear close timeout from button when mouse enters menu
    this.settings.gearButton.clearCloseTimeout()
  }

  handleMouseLeave() {
    this.settings.gearButton.scheduleClose()
  }

  closeMenu() {
    // Clear close timeout when menu is closed
    this.settings.gearButton.clearCloseTimeout()

    this.mainMenu.deactivate()
    this.audioMenu.deactivate()
    this.subtitlesMenu.deactivate()
    this.subtitlesLanguageMenu.deactivate()
    this.subtitlesCustomMenu.deactivate()
    this.qualityMenu.deactivate()
    this.settings.gearButton.pressed(false)
    this.hide()

    // height transition reset
    this.el_.style.height = '0'
  }

  override dispose() {
    if (
      this.isDesktop_ &&
      this.handleMouseEnter_ &&
      this.handleMouseLeave_ &&
      this.el_
    ) {
      this.el_.removeEventListener('mouseenter', this.handleMouseEnter_)
      this.el_.removeEventListener('mouseleave', this.handleMouseLeave_)
    }
    super.dispose()
  }
}

SettingsMenuContainer.options = {
  className: 'vjs-x-settings-menu-container',
  children: [
    // settings main menu
    SettingsMainMenu.as('mainMenu'),

    // settings subtitles menu
    SettingsSubtitlesMenu.as('subtitlesMenu'),
    SettingsSubtitlesLanguageMenu.as('subtitlesLanguageMenu', {
      title: 'language',
    }),
    SettingsSubtitlesCustomMenu.as('subtitlesCustomMenu', {
      title: 'custom_setting',
    }),

    // settings audio menu
    SettingsAudioMenu.as('audioMenu', { title: 'Audio' }),

    // settings quality menu
    SettingsQualityMenu.as('qualityMenu', { title: 'quality' }),
  ],
}
